// llmService.js — thin client
//
// Sends chat messages to our Vercel serverless proxy at /api/llm/chat.
// The proxy handles the actual provider chain (Groq → Cerebras → SambaNova →
// Google → Z.ai → Cloudflare), keeps API keys server-side only, and enforces
// per-user usage quotas.
//
// Public API:
//   callLLM({ messages, temperature, max_tokens, preferredProvider })  →  string
//   callLLMWithMeta({ messages, temperature, max_tokens, preferredProvider })
//     →  { content, provider, model }
//
// `preferredProvider` is optional — pass the provider id (e.g. "groq") or
// display name (e.g. "Groq") returned from a previous callLLMWithMeta() call
// to ask the proxy to try that same provider first. Useful for a "continue
// where you left off" follow-up call, so the continuation is more likely to
// come from the SAME model that wrote the first half (keeping voice and
// formatting consistent) — the proxy still falls back to the rest of the
// chain if that provider isn't available.
//
// On success: callLLM returns the assistant's text content (string).
// On failure: throws an Error with a useful message.
// On missing/expired session: throws an Error with .code === 'NOT_AUTHENTICATED'.
// On quota exceeded: throws an Error with .code === 'QUOTA_EXCEEDED' and .resetAt.

import { supabase } from '../lib/supabaseClient.js';

export async function callLLMWithMeta({ messages, temperature = 0.7, max_tokens = 2000, preferredProvider } = {}) {
    // Get current session to authenticate this request to our proxy.
    // supabase.auth.getSession() already attempts a silent token refresh
    // internally if the access token is expired but the refresh token is
    // still valid — a null session here means there's genuinely no valid
    // session (never logged in on this origin, or the refresh token itself
    // expired), not a transient failure. Callers should prompt re-login,
    // not retry the same request.
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
        const err = new Error('Not signed in — please log in again.');
        err.code = 'NOT_AUTHENTICATED';
        throw err;
    }

    const response = await fetch('/api/llm/chat', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ messages, temperature, max_tokens, preferredProvider }),
    });

    // Session was valid client-side but the proxy rejected the token
    // (expired between getSession() and the request, revoked, etc.) —
    // same recovery action as a missing session: log in again, don't retry.
    if (response.status === 401) {
        const errBody = await response.json().catch(() => ({}));
        const err = new Error(errBody.error || 'Not signed in — please log in again.');
        err.code = 'NOT_AUTHENTICATED';
        throw err;
    }

    // Quota exceeded — surface a structured error so the UI can show the right message
    if (response.status === 429) {
        const errBody = await response.json().catch(() => ({}));
        const err = new Error(
            errBody.message ||
            `You've reached your ${errBody.reason === 'hourly_limit' ? 'hourly' : 'daily'} usage limit.`
        );
        err.code = 'QUOTA_EXCEEDED';
        err.reason = errBody.reason;            // 'hourly_limit' or 'daily_limit'
        err.resetAt = errBody.resetAt;          // ISO timestamp when quota refreshes
        err.remaining = errBody.remaining;      // { hour, day }
        throw err;
    }

    // Other non-2xx → extract useful error from proxy
    if (!response.ok) {
        let detail = '';
        try {
            const errBody = await response.json();
            detail = errBody?.error || JSON.stringify(errBody);
        } catch (_) {
            detail = await response.text().catch(() => response.statusText);
        }
        throw new Error(`LLM proxy error ${response.status}: ${detail}`);
    }

    const data = await response.json();
    if (typeof data?.content !== 'string') {
        throw new Error('LLM proxy returned no content');
    }

    if (data.provider) {
        console.log(`[LLM] Answered by ${data.provider} (${data.model})`);
    }

    return { content: data.content, provider: data.provider, model: data.model };
}

export async function callLLM(options) {
    const { content } = await callLLMWithMeta(options);
    return content;
}

/**
 * Helper for UI components — formats a quota-exceeded error into a user-readable message.
 * Usage:
 *   try { await callLLM(...) }
 *   catch (e) {
 *     if (e.code === 'QUOTA_EXCEEDED') alert(formatQuotaError(e));
 *   }
 */
export function formatQuotaError(err) {
    if (err?.code !== 'QUOTA_EXCEEDED') return err?.message || 'Unknown error';

    const resetAt = new Date(err.resetAt);
    const minutesUntilReset = Math.max(1, Math.ceil((resetAt - new Date()) / 60000));

    if (err.reason === 'hourly_limit') {
        return `You've used your hourly AI limit. Resets in ${minutesUntilReset} minutes.`;
    }
    if (err.reason === 'daily_limit') {
        const hoursUntilReset = Math.ceil(minutesUntilReset / 60);
        return `You've used your daily AI limit. Resets in ${hoursUntilReset} hours.`;
    }
    return 'Usage limit reached. Please try again later.';
}